import { FC, ReactNode } from "react";

import { ButtonGroup } from "@/components/_atoms/button-group";
import { TableEmptyStateProps } from "@/components/_molecules/table-empty-state/table-empty-state";
import { useFeatureFlagSimple } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-simple";
import { buildDatatableToolbarRowClass } from "@/domain/view/components/datatable/datatable-toolbar-row-class";
import { WorkspaceStoredViewInput } from "@/domain/view/models/stored-view-type";
import { TeamStoredViewInput } from "@/domain/view/models/stored-view-type";

import { StoredViewListMenu } from "../../stored-view-menu/stored-view-list-menu";
import { AddAdditionalFilterButton } from "../datatable-filter-dropdown/add-additional-filter-button";
import { DatatableQuickSearch } from "../datatable-quicksearch";
import { defaultValueIfUndefined } from "../datatable-utils/config-helpers";

import { DatatableCsvExportButton } from "./datatable-csv-export-button";
import { DatatableDisplayOptionsButton } from "./datatable-display-options-button";

export interface ConfigurationViewConfig {
  totalRow?: ReactNode;
  noInset?: boolean;
  emptyState?: TableEmptyStateProps;
  quicksearch?: boolean;
  filterButton?: boolean;
  displayOptionsButton?: boolean;
  hideGrouping?: boolean;
  hideSubgrouping?: boolean;
  hideSorting?: boolean;
  hideDatatableOptions?: boolean;
  hideListAndDisplaySettings?: boolean;
  disableClickableRows?: boolean;
  lockPublishedConfiguration?: boolean;
  displayOptionsCustomContent?: ReactNode;
  customToolbarButtons?: ReactNode;
  leftToolbarContent?: ReactNode;
  appliedFilterRightContent?: ReactNode;
  csvExport?:
    | {
        onExport: (decimalSeparator: "comma" | "dot") => void;
        isDisabled?: boolean;
        exportLabel?: string;
        successMessage?: string;
        errorMessage?: string;
      }
    | false;
  storedViewMenu?:
    | undefined
    | null
    | {
        workspace: {
          id: string;
        };
        user: {
          id: string;
        };
        canBeShared: TeamStoredViewInput | WorkspaceStoredViewInput | null | undefined;
      };
}

export interface DatatableConfigurationViewProps {
  config?: ConfigurationViewConfig;
}

export const DatatableConfigurationView: FC<DatatableConfigurationViewProps> = ({ config = {} }) => {
  const { isEnabled: isDatatableCsvExportEnabled } = useFeatureFlagSimple({
    id: "enable-datatable-csv-export",
  });

  const shoulRenderQuicksearch = defaultValueIfUndefined<boolean>({ val: config.quicksearch, defaultVal: true });
  const shoulRenderDisplayOptionsButton = defaultValueIfUndefined<boolean>({
    val: config.displayOptionsButton,
    defaultVal: true,
  });

  const shouldRenderCsvExport =
    isDatatableCsvExportEnabled && config.csvExport !== false && config.csvExport !== undefined;

  const shouldRenderFilterButton = defaultValueIfUndefined<boolean>({ val: config.filterButton, defaultVal: true });

  const hasStoredViewMenu = !!config?.storedViewMenu;
  const hasSearchGroup = shoulRenderQuicksearch || shouldRenderFilterButton || shoulRenderDisplayOptionsButton;
  const hasAnyFeature =
    hasSearchGroup || hasStoredViewMenu || shouldRenderCsvExport || !!config.leftToolbarContent;

  if (!hasAnyFeature) {
    return null;
  }

  return (
    <div
      className={buildDatatableToolbarRowClass(!!config.noInset)}
      data-testid="datatable-configuration-view"
    >
      <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
        {hasSearchGroup && (
          <ButtonGroup className={shoulRenderQuicksearch ? "w-full min-w-0 flex-1" : undefined}>
            {shoulRenderQuicksearch && <DatatableQuickSearch />}
            {shouldRenderFilterButton && <AddAdditionalFilterButton />}
            {shoulRenderDisplayOptionsButton && (
              <DatatableDisplayOptionsButton
                hideGrouping={config.hideGrouping}
                hideSubgrouping={config.hideSubgrouping}
                hideSorting={config.hideSorting}
                hideDatatableOptions={config.hideDatatableOptions}
                hideListAndDisplaySettings={config.hideListAndDisplaySettings}
                customContent={config.displayOptionsCustomContent}
              />
            )}
          </ButtonGroup>
        )}
        {config.leftToolbarContent}
      </div>
      <div className="flex flex-row items-center gap-2">
        {config?.storedViewMenu && (
          <StoredViewListMenu
            user={config.storedViewMenu.user}
            workspace={config.storedViewMenu.workspace}
            canBeShared={config.storedViewMenu.canBeShared}
          />
        )}
        {shouldRenderCsvExport && config.csvExport && (
          <DatatableCsvExportButton
            onExport={config.csvExport.onExport}
            isDisabled={config.csvExport.isDisabled}
            exportLabel={config.csvExport.exportLabel}
            successMessage={config.csvExport.successMessage}
            errorMessage={config.csvExport.errorMessage}
            displayMode="toolbar"
          />
        )}
        {config.customToolbarButtons}
      </div>
    </div>
  );
};
